import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { currentTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: currentTheme.background }}
    >
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 mb-8 font-body opacity-70 hover:opacity-100 transition-opacity"
          style={{ color: currentTheme.foreground }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        <div 
          className="rounded-2xl p-8"
          style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.08)' }}
        >
          {submitted ? (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: `${currentTheme.primary}15` }}
              >
                <CheckCircle className="w-8 h-8" style={{ color: currentTheme.primary }} />
              </div>
              <h1 className="font-display text-2xl font-medium mb-3" style={{ color: currentTheme.foreground }}>
                Check Your Email
              </h1>
              <p className="font-body opacity-70 mb-6">
                If an account exists with {email}, you'll receive password reset instructions shortly.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Return to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div 
                  className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}15` }}
                >
                  <Mail className="w-7 h-7" style={{ color: currentTheme.primary }} />
                </div>
                <h1 className="font-display text-2xl font-medium" style={{ color: currentTheme.foreground }}>
                  Forgot Password?
                </h1>
                <p className="font-body opacity-70 mt-2">
                  No worries, we'll send you reset instructions.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-body font-medium">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12"
                  style={{ backgroundColor: currentTheme.primary }}
                  disabled={loading || !email}
                  data-testid="forgot-password-submit"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
